import React, { useState } from "react";
import {
  GridColDef,
  GridState,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useQuery } from "@apollo/client";
import { Box, Stack, TextField, Card, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { parseISO, sub, format, addDays } from "date-fns";
import {
  GetDealsMetricsDocument,
  GetDealsMetricsQuery,
} from "../../generated/graphql-operations";
import { checkAndFormatDate } from "../../utils/DateUtils";
import {
  AU_DATE_FORMAT,
  VIRTUAL_UNIT_LABEL_SUFFIX,
} from "../../utils/CommonVariables";
import { formatter, formatPercent } from "../../utils/CommonFunctions";
import PersistedDataGrid from "../PersistedDataGrid";
import {
  getTotalAchievedRent,
  getTotalBudgetRent,
  getTotalAchievedRentVariance,
  getBaseRentValuation,
  getAchievedRentValuationVariance,
  getTotalCurrentRent,
  getAchievedToCurrentRentVariance,
  getAchievedTotalCapital,
  getBudgetTotalCapital,
  getAchievedToBudgetCapitalVariance,
} from "../../utils/DealUtils";
import DealsMetricsSummary from "./DealsMetricsSummary";

export interface DealsMetricsListProps {
  toolBar: boolean;
}

const CustomToolbar = () => {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2}>
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </Stack>
      <GridToolbarQuickFilter variant="filled" />
    </Stack>
  );
};

export const DealsMetricsList = ({ toolBar }: DealsMetricsListProps) => {
  const apiRef = useGridApiRef();

  const [startDate, setStartDate] = useState<Date>(() => {
    const storedStart = sessionStorage.getItem("startDate");
    return storedStart ? new Date(storedStart) : sub(new Date(), { months: 6 });
  });

  const [endDate, setEndDate] = useState<Date>(() => {
    const storedEnd = sessionStorage.getItem("endDate");
    return storedEnd ? new Date(storedEnd) : new Date();
  });
  const { data, loading, error } = useQuery(GetDealsMetricsDocument, {
    variables: {
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      filter_type: "LAF_APPROVAL",
    },
  });
  const [filteredDeals, setFilteredDeals] = useState<
    GetDealsMetricsQuery["Deals"] | GridValidRowModel[]
  >(data?.Deals ?? []);

  React.useEffect(() => {
    if (data)
      // eslint-disable-next-line no-console
      console.log("[DealsMetricsList] deals:", data.Deals?.length ?? 0);
  }, [data]);

  const handleStateChange = (newState: GridState) => {
    const rows = newState?.rows?.dataRowIdToModelLookup;
    const filteredRowsLookup = newState?.filter?.filteredRowsLookup;
    const filtered = Object.values(rows).filter(
      (row) => filteredRowsLookup[row.id]
    );

    if (filtered.length !== filteredDeals.length) {
      setFilteredDeals(filtered);
    }
  };

  const navigate = useNavigate();

  const columns: GridColDef<NonNullable<GetDealsMetricsQuery["Deals"][0]>>[] = [
    // Deal Type
    {
      field: "dealType",
      headerName: "Deal Type",
      flex: 1,
      valueGetter: (params) => params.row.DealType?.title ?? "",
    },
    // Property Code
    {
      field: "propertyCode",
      headerName: "Property code",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.Property?.property_code ??
        params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.Property
          ?.property_code ??
        "",
    },
    // Property Name
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.Property?.label ??
        params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.Property?.label ??
        "",
    },
    // TenantId
    {
      field: "tenant_code",
      headerName: "Tenant ID",
      flex: 1,
      valueGetter: (params) =>
        params.row.tenant_code ??
        params.row.YardiTenants_Deals?.[0]?.TenantCode ??
        "",
    },
    // Trading Name
    {
      field: "tradingName",
      headerName: "Trading name",
      flex: 1,
      valueGetter: (params) => params.row.trading_name,
    },
    // Deal Owner
    {
      field: "dealOwner",
      headerName: "Deal owner",
      flex: 1,
      valueGetter: (params) => params.row.Owner?.user_full_name ?? "",
    },
    // Unit Name
    {
      field: "shop",
      headerName: "Shop",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.label ??
        (params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.label
          ? `${params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.label}${VIRTUAL_UNIT_LABEL_SUFFIX}`
          : null) ??
        "",
    },
    // Unit Area
    {
      field: "unitArea",
      headerName: "Unit area",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits?.[0]?.Unit?.UnitArea ?? "",
    },
    // Lease Start Date
    {
      field: "leaseStart",
      headerName: "Lease start date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealData[0]?.lease_start
          ? parseISO(params.row.DealData[0]?.lease_start)
          : undefined,
    },
    // LAF Approval Date
    {
      field: "LafApprovalDate",
      headerName: "LAF Approval Date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) => {
        return params.row.ActivityLogs?.[0]?.created_at
          ? parseISO(params.row.ActivityLogs?.[0]?.created_at)
          : undefined;
      },
    },
    // Achieved Gross Rent
    {
      field: "achievedGrossRent",
      headerName: "Achieved Gross Rent",
      flex: 1,
      valueGetter: (params) =>
        formatter(getTotalAchievedRent(params.row.DealData[0]), "$"),
    },
    // Budget Gross Rent
    {
      field: "budgetGrossRent",
      headerName: "Budget Gross Rent",
      flex: 1,
      valueGetter: (params) =>
        formatter(getTotalBudgetRent(params.row.DealData[0]), "$"),
    },
    // Variance between Achieved Rent to Budget Rent
    {
      field: "varianceToBudget",
      headerName: "Variance to budget",
      flex: 1,
      valueGetter: (params) => {
        const variance = getTotalAchievedRentVariance(params.row.DealData[0]);
        return formatPercent(variance);
      },
    },
    // Valuation Rent
    {
      field: "valuationRent",
      headerName: "Valuation Rent",
      flex: 1,
      valueGetter: (params) =>
        formatter(getBaseRentValuation(params.row.DealData[0]), "$"),
    },
    // Variance between Achieved Rent to Valuation Rent
    {
      field: "varianceToValuation",
      headerName: "Variance To Valuation",
      flex: 1,
      valueGetter: (params) => {
        const variance = getAchievedRentValuationVariance(
          params.row.DealData[0]
        );
        return formatPercent(variance);
      },
    },
    // Current/Previous Rent
    {
      field: "currentRent",
      headerName: "Current/Previous Rent",
      flex: 1,
      valueGetter: (params) =>
        formatter(getTotalCurrentRent(params.row.DealData[0]), "$"),
    },
    // Variance between Achieved Rent to Current/Previous Rent
    {
      field: "varianceToCurrentRent",
      headerName: "Variance To Current Rent",
      flex: 1,
      valueGetter: (params) => {
        const variance = getAchievedToCurrentRentVariance(
          params.row.DealData[0]
        );
        return formatPercent(variance);
      },
    },
    // Achieved Total Capital
    {
      field: "achievedTotalCapital",
      headerName: "Achieved Total Capital",
      flex: 1,
      valueGetter: (params) =>
        formatter(getAchievedTotalCapital(params.row.DealData[0]), "$"),
    },
    // Budget Total Capital
    {
      field: "budgetTotalCapital",
      headerName: "Budget Total Capital",
      flex: 1,
      valueGetter: (params) =>
        formatter(getBudgetTotalCapital(params.row.DealData[0]), "$"),
    },
    // Variance between Achieved Capital to Budget Capital
    {
      field: "VarianceAchievedToBudgetCapital",
      headerName: "Variance To Budget Capital",
      flex: 1,
      valueGetter: (params) => {
        const variance = getAchievedToBudgetCapitalVariance(
          params.row.DealData[0]
        );
        return formatPercent(variance);
      },
    },
    // Proposed Handover
    {
      field: "proposedHandoverDate",
      headerName: "Proposed Handover Date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealData?.[0]?.handover_date
          ? parseISO(params.row.DealData?.[0]?.handover_date)
          : undefined,
    },
    // Actual Handover Date (based on when the notification sent)
    {
      field: "actualHandoverDate",
      headerName: "Actual Handover Date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealShopActivities?.[0]?.actual_handover_date
          ? parseISO(params.row.DealShopActivities?.[0]?.actual_handover_date)
          : undefined,
    },
    // Proposed Shop Open Date proposed shop open will be proposed handover date plus fitout period in weeks
    {
      field: "proposedShopOpenDate",
      headerName: "Proposed Shop Open Date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) => {
        return params.row.DealData?.[0]?.handover_date
          ? addDays(
              parseISO(params.row.DealData?.[0]?.handover_date),
              params.row.DealData?.[0]?.fitout_term ?? 0
            )
          : undefined;
      },
    },
    // Actual Shop Open Date (based on when the notification sent)
    {
      field: "actualShopOpenDate",
      headerName: "Actual Shop Open Date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealShopActivities?.[0]?.open_date
          ? parseISO(params.row.DealShopActivities?.[0]?.open_date)
          : undefined,
    },
    // TD Assignee
    {
      field: "tdAssignee",
      headerName: "TD assignee",
      flex: 1,
      valueGetter: (params) => params.row.TD_Assignee?.user_full_name ?? "",
    },
  ];

  return (
    <Card elevation={10} sx={{ p: 3, mt: 3 }}>
      <DealsMetricsSummary filteredDeals={filteredDeals} />
      <Box mb={2} gap={2} display="flex" justifyContent="flex-end">
        <Box>
          <Typography gutterBottom>LAF Approval Date Range</Typography>
          <Stack direction="row" spacing={2}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(newValue) => {
                if (
                  newValue instanceof Date &&
                  !Number.isNaN(newValue.getTime())
                ) {
                  setStartDate(newValue);
                  sessionStorage.setItem("startDate", newValue.toISOString());
                }
              }}
              minDate={new Date("2000-01-01")}
              renderInput={(params) => (
                <TextField {...params} variant="filled" color="primary" />
              )}
            />
            <DatePicker
              label="To"
              value={endDate}
              onChange={(newValue) => {
                if (
                  newValue instanceof Date &&
                  !Number.isNaN(newValue.getTime())
                ) {
                  setEndDate(newValue);
                  sessionStorage.setItem("endDate", newValue.toISOString());
                }
              }}
              renderInput={(params) => (
                <TextField {...params} variant="filled" color="primary" />
              )}
            />
          </Stack>
        </Box>
      </Box>
      <Box style={{ flexGrow: 1 }}>
        <PersistedDataGrid
          classes={{
            sortIcon: "color:red;margin-top:75px;",
          }}
          apiRef={apiRef}
          rows={data?.Deals ?? []}
          columns={columns}
          loading={loading || error !== undefined}
          onCellClick={(params, event) => {
            if (!event.ctrlKey) {
              navigate(`/deals/${params.row.id}`);
            }
          }}
          slots={toolBar ? { toolbar: CustomToolbar } : {}}
          onStateChange={handleStateChange}
        />
      </Box>
    </Card>
  );
};

export default DealsMetricsList;
